import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ImageUploader from './components/ImageUploader';
import QuoteCard from './components/QuoteCard';
import LoadingOverlay from './components/LoadingOverlay';

export interface EstimateResult {
  customerExpectations: string;
  timeEstimate: string;
  numberOfVisits: string;
  status: 'ready_to_send' | 'needs_review' | 'info_missing';
  summary: string[];
  missingDetails: string[];
  lineItems: {
    category: 'trip_fee' | 'labor' | 'materials' | 'paint_finishing' | 'other';
    label: string;
    unit: string;
    quantity: number;
    rate: number;
  }[];
  notes: string[];
  standardNotes: string[];
  estimatedReviewTime?: string;
  priceRange: {
    min: number;
    max: number;
  };
  customerMaterials: string[];
  confidenceScore: 'low' | 'medium' | 'high';
  confidenceReason?: string;
}

type Step = 'upload' | 'describe' | 'contact' | 'result';

const trackEstimatorEvent = (eventName: string, params: Record<string, unknown> = {}) => {
  if (typeof window === 'undefined') return;
  const tracker = (window as any).trackPalmetteauxEvent;
  if (typeof tracker === 'function') {
    tracker(eventName, { location: 'estimator', ...params });
    return;
  }
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ event: eventName, location: 'estimator', ...params });
};

const App: React.FC = () => {
  const [step, setStep] = useState<Step>('upload');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [userDescription, setUserDescription] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [estimate, setEstimate] = useState<EstimateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Auto-advance when image is selected
  const handleImageSelect = useCallback((file: File) => {
    trackEstimatorEvent('estimator_photo_added', { file_type: file.type || 'unknown' });
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
      setStep('describe'); // Auto-advance
    };
    reader.readAsDataURL(file);
    setEstimate(null);
    setError(null);
  }, []);

  const handleRemoveImage = useCallback(() => {
    setImageFile(null);
    setImagePreview(null);
    setEstimate(null);
    setError(null);
    setStep('upload');
  }, []);

  const handleReset = useCallback(() => {
    setImageFile(null);
    setImagePreview(null);
    setEstimate(null);
    setError(null);
    setUserDescription('');
    setFullName('');
    setPhoneNumber('');
    setEmail('');
    setConsent(false);
    setStep('upload');
  }, []);

  const analyzeRequest = async () => {
    setIsAnalyzing(true);
    setError(null);
    trackEstimatorEvent('estimator_submit', {
      has_image: Boolean(imageFile),
      has_email: Boolean(email.trim())
    });

    const formData = new FormData();
    if (imageFile) {
      formData.append('image', imageFile);
    }
    formData.append('description', userDescription);
    formData.append('phone', phoneNumber);
    if (fullName.trim()) {
      formData.append('name', fullName.trim());
    }
    if (email.trim()) {
      formData.append('email', email.trim());
    }

    try {
      const response = await fetch('/api/estimate', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get estimate');
      }

      const data = await response.json();
      setEstimate(data);
      setStep('result');
      trackEstimatorEvent('estimator_completed', {
        status: data.status || 'unknown',
        has_image: Boolean(imageFile)
      });
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Something went wrong. Please try again.');
      trackEstimatorEvent('estimator_error', { message: err.message || 'unknown' });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const renderStepIndicator = () => {
    const steps: Step[] = ['upload', 'describe', 'contact'];
    if (step === 'result') return null;

    return (
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-2">
          {steps.map((s, idx) => {
            const isActive = s === step;
            const isCompleted = steps.indexOf(step) > idx;
            return (
              <React.Fragment key={s}>
                <div
                  className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${
                    isActive || isCompleted ? 'bg-brand-600 scale-110' : 'bg-highlight'
                  }`}
                />
                {idx < steps.length - 1 && (
                  <div
                    className={`h-0.5 w-8 transition-colors duration-300 ${
                      isCompleted ? 'bg-brand-600' : 'bg-highlight'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 min-h-[600px] flex flex-col justify-center">
      {step !== 'result' && (
        <div className="text-center space-y-3 animate-fade-in">
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-600">
            AI Estimator
          </p>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-charcoal">
            Get a fast, no-pressure estimate
          </h1>
          <p className="text-lg text-charcoal/70 max-w-xl mx-auto">
            Tell me what’s going on and add a few photos. I’ll build a clear, ballpark price for
            you.
          </p>
          <p className="text-sm text-charcoal/60">
            AI-powered quote generation from your inputs, reviewed by Chris before booking.
          </p>
        </div>
      )}

      {renderStepIndicator()}

      <div className="card-surface relative overflow-hidden">
        <AnimatePresence mode="wait">
          {step === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <ImageUploader
                onImageSelect={handleImageSelect}
                selectedImage={imagePreview}
                onRemove={handleRemoveImage}
              />
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    trackEstimatorEvent('estimator_start', { method: 'description_only' });
                    setStep('describe');
                  }}
                  className="text-sm text-charcoal/60 hover:text-brand-600 underline decoration-1 underline-offset-4 transition-colors"
                >
                  Skip photo, I'll describe it
                </button>
              </div>
            </motion.div>
          )}

          {step === 'describe' && (
            <motion.div
              key="describe"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-6"
            >
              {imagePreview && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="flex items-center gap-4 p-3 bg-highlight/30 rounded-xl border border-highlight/60"
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded-lg shadow-sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-charcoal truncate">Photo attached</p>
                    <button
                      onClick={handleRemoveImage}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </motion.div>
              )}

              <div className="space-y-2">
                <label htmlFor="description" className="block text-sm font-medium text-charcoal/80">
                  Description {imageFile ? '(Optional)' : '(Required)'}
                </label>
                <textarea
                  id="description"
                  rows={4}
                  autoFocus
                  className="w-full rounded-xl border-highlight shadow-inner-light focus:border-brand-500 focus:ring-brand-500 p-4 border text-lg transition-shadow"
                  placeholder={
                    imageFile
                      ? "e.g., It's about 6 feet high..."
                      : 'e.g., I have a hole in the drywall...'
                  }
                  value={userDescription}
                  onChange={(e) => setUserDescription(e.target.value)}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep('upload')}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-charcoal/70 hover:bg-highlight/50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    trackEstimatorEvent('estimator_description_complete', {
                      has_image: Boolean(imageFile)
                    });
                    setStep('contact');
                  }}
                  disabled={!imageFile && userDescription.trim().length < 5}
                  className={`flex-1 py-3 px-4 rounded-xl font-bold text-white shadow-lg transition-all transform ${
                    imageFile || userDescription.trim().length >= 5
                      ? 'bg-brand-600 hover:bg-brand-500 hover:-translate-y-0.5 shadow-brand-600/30'
                      : 'bg-charcoal/30 cursor-not-allowed'
                  }`}
                >
                  Next Step
                </button>
              </div>
            </motion.div>
          )}

          {step === 'contact' && (
            <motion.div
              key="contact"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-6"
            >
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="fullName" className="block text-sm font-medium text-charcoal/80">
                    Your Name
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    autoFocus
                    className="w-full rounded-xl border-highlight shadow-inner-light focus:border-brand-500 focus:ring-brand-500 p-4 border text-lg"
                    placeholder="e.g., Chris Wade"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-sm font-medium text-charcoal/80">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    className="w-full rounded-xl border-highlight shadow-inner-light focus:border-brand-500 focus:ring-brand-500 p-4 border text-lg"
                    placeholder="(864) 555-0123"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-charcoal/80">
                    Email <span className="text-charcoal/40 font-normal">(Optional)</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="w-full rounded-xl border-highlight shadow-inner-light focus:border-brand-500 focus:ring-brand-500 p-4 border text-lg"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <label className="flex items-start gap-3 text-sm text-charcoal/70 p-3 bg-highlight/20 rounded-xl cursor-pointer hover:bg-highlight/40 transition-colors">
                  <input
                    type="checkbox"
                    className="mt-1 h-5 w-5 rounded border-charcoal/40 text-brand-600 focus:ring-brand-500"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span>
                    I agree to receive texts from Palmetteaux Services about my estimate request,
                    scheduling, and related services. Message frequency varies. Message and data
                    rates may apply. Reply STOP to unsubscribe or HELP for help. See our{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 text-red-700 rounded-xl text-sm font-medium border border-red-100"
                >
                  {error}
                </motion.div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep('describe')}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-charcoal/70 hover:bg-highlight/50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={analyzeRequest}
                  disabled={!fullName.trim() || !phoneNumber.trim() || !consent || isAnalyzing}
                  className={`flex-[2] py-4 px-6 rounded-xl font-bold text-white text-lg shadow-xl transition-all transform ${
                    fullName.trim() && phoneNumber.trim() && consent && !isAnalyzing
                      ? 'bg-brand-600 hover:bg-brand-500 hover:-translate-y-0.5 shadow-brand-600/30 cta-glow'
                      : 'bg-charcoal/30 cursor-not-allowed'
                  }`}
                >
                  {isAnalyzing ? 'Analyzing...' : 'Get My Estimate'}
                </button>
              </div>
            </motion.div>
          )}

          {step === 'result' && estimate && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <QuoteCard estimate={estimate} onReset={handleReset} imageFile={imageFile} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {isAnalyzing && <LoadingOverlay />}
    </div>
  );
};

export default App;
